import { View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import type { VerificationStatus } from '@/api/partners/organizations/types';
import { Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Cancel } from '@/ui/icons/cancel';
import { Checkmark } from '@/ui/icons/checkmark';
import { Minus } from '@/ui/icons/minus';
import { Pending } from '@/ui/icons/pending';

const STATUS_ICON: Record<VerificationStatus, React.ReactNode> = {
  empty: <Minus />,
  pending: <Pending />,
  verified: <Checkmark />,
  rejected: <Cancel />,
};

const useStatusColor = () => {
  const { theme } = useStyles();

  return (status: VerificationStatus) => {
    switch (status) {
      case 'pending':
        return theme.colors.icon.attentionColor;
      case 'verified':
        return theme.colors.icon.successColor;
      case 'rejected':
        return theme.colors.icon.errorColor;
      default:
        return theme.colors.text.secondary50;
    }
  };
};

export const StatusIcon = ({
  status,
  size = 16,
}: {
  status: VerificationStatus;
  size?: number;
}) => {
  const getColor = useStatusColor();

  return (
    <Icon
      type="naked"
      icon={STATUS_ICON[status]}
      fillColor={getColor(status)}
      width={size}
      height={size}
    />
  );
};

const LEGEND: { status: VerificationStatus; label: string }[] = [
  { status: 'empty', label: 'Nothing submitted yet' },
  { status: 'pending', label: 'Pending (awaiting review)' },
  { status: 'verified', label: 'Verified (approved)' },
  { status: 'rejected', label: 'Rejected (declined)' },
];

export const CustomerStatusLegend = () => {
  const { theme } = useStyles();

  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 16,
      }}
    >
      {LEGEND.map((item) => (
        <View
          key={item.status}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
        >
          <StatusIcon status={item.status} size={14} />

          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {item.label}
          </Text>
        </View>
      ))}
    </View>
  );
};
